import { useState, useEffect, useRef } from 'react';
import { useResponsive, getResponsiveGridClasses, getResponsiveTextClasses, getResponsiveSpacingClasses } from '@/lib/responsive-utils';
import type { StatisticItem } from '../../types';

interface StatisticsProps {
  statistics: StatisticItem[];
  className?: string;
}

interface AnimatedCounterProps {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  isVisible: boolean;
}

function AnimatedCounter({ value, prefix = '', suffix = '', duration = 2000, isVisible }: AnimatedCounterProps) {
  const [currentValue, setCurrentValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    setHasAnimated(true);
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(startValue + (value - startValue) * easeOutQuart);
      
      setCurrentValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, value, duration, hasAnimated]);

  const formatNumber = (num: number) => {
    return num.toLocaleString('es-MX');
  };

  return (
    <span className="font-bold text-white"
    style={{ 
      fontSize: 'clamp(3rem, 8vw, 6rem)' // Mínimo 3rem, máximo 6rem, escalable
    }}>
      {prefix}{formatNumber(currentValue)}{suffix}
    </span>
  );
}

export function Statistics({ statistics, className = '' }: StatisticsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Disconnect observer after first trigger to prevent re-animation
          observer.disconnect();
        }
      },
      {
        threshold: 0.3, // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before the section is fully visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={`${getResponsiveSpacingClasses('py', 8, 8, 16)} relative ${className}`}
      style={{
        backgroundImage: 'url(/fondos/02_Fondo_.Numeralia.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay para mejorar legibilidad del texto */}
      <div className="absolute inset-0 bg-liec-primary-500/10" />
      
      <div className="w-full ">
        {/* Section Header 
        <div className={`text-center ${getResponsiveSpacingClasses('mb', 6, 6, 8)}`}>
          
          
        </div>
*/}
        {/* Statistics Grid */}
        <div className={getResponsiveGridClasses(1, 2, 4)} style={{ gap: isMobile ? '1rem' : '1rem' }}>
          {statistics.map((stat, index) => (
            <div
              key={`${stat.label}-${index}`}
              className="text-center group"
            >
              {/* Counter */}
              <div className={getResponsiveSpacingClasses('mb', 1, 1, 1)}>
                <AnimatedCounter
                  value={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  isVisible={isVisible}
                  duration={2000 + index * 200} // Stagger animation slightly
                />
              </div>

              {/* Label */}
              <h3 className={`${getResponsiveTextClasses('text-base', 'text-lg', 'text-xl')} font-medium text-white leading-tight`}>
                {stat.label}
              </h3>

              {/* Decorative line */}
              <div className="w-12 h-1 bg-white mx-auto mt-4 rounded-full group-hover:w-16 transition-all duration-300" />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}